import React, { useState, useEffect, useCallback, useRef } from "react";
import { Document, Page } from "react-pdf";
import { Button } from "@/dentlab/src/components/ui/button";
import {
	ChevronLeft,
	ChevronRight,
	Loader2,
	ZoomIn,
	ZoomOut,
	MoveHorizontal,
	MoveVertical,
	Printer,
	Download,
} from "lucide-react";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "@/lib/utils/pdf-worker";
import { Logger } from "@/lib/logger/Logger";
import {
	dispatchPrint,
	getResolvedPrintMode,
} from "@/dentlab/src/lib/utils/print-dispatch";

interface PdfFullViewerProps {
	url: string;
	onDownload?: () => void;
}

const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3];

export const PdfFullViewer: React.FC<PdfFullViewerProps> = ({ url, onDownload }) => {
	const [numPages, setNumPages] = useState<number | null>(null);
	const [pageNumber, setPageNumber] = useState(1);
	const [zoom, setZoom] = useState<number>(1); // Always a number now
	const [pageHeight, setPageHeight] = useState<number | null>(null);
	const [pageWidth, setPageWidth] = useState<number | null>(null);
	const [fitMode, setFitMode] = useState<"height" | "width" | null>("height");
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	// Calculate page dimensions based on zoom level and fit mode
	const calculatePageDimensions = useCallback(() => {
		if (!containerRef.current) return;

		const container = containerRef.current;
		const containerHeight = container.clientHeight - 120; // Account for navigation and padding
		const containerWidth = container.clientWidth - 80; // Account for padding

		if (fitMode === "height") {
			setPageHeight(containerHeight);
			setPageWidth(null);
		} else if (fitMode === "width") {
			setPageWidth(containerWidth);
			setPageHeight(null);
		} else {
			// Manual zoom mode
			const baseHeight = containerHeight;
			setPageHeight(baseHeight * zoom);
			setPageWidth(null);
		}
	}, [zoom, fitMode]);

	// Recalculate dimensions on mount and resize
	useEffect(() => {
		calculatePageDimensions();

		const handleResize = () => {
			calculatePageDimensions();
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [calculatePageDimensions]);

	useEffect(() => {
		setIsLoading(true);
		setHasError(false);
		setFitModeAutoSet(false);
	}, [url]);

	const [fitModeAutoSet, setFitModeAutoSet] = useState(false);

	const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
		setNumPages(numPages);
		setPageNumber(1);
		setIsLoading(false);
		setHasError(false);
		setFitModeAutoSet(false);
	};

	const onPageRenderSuccess = useCallback((page: any) => {
		if (!fitModeAutoSet && page?.originalWidth && page?.originalHeight) {
			const isLandscape = page.originalWidth > page.originalHeight;
			setFitMode(isLandscape ? "width" : "height");
			setFitModeAutoSet(true);
		}
	}, [fitModeAutoSet]);

	const onDocumentLoadError = (error: Error) => {
		Logger.error("PDF loading error:" + error);
		setHasError(true);
		setTimeout(() => {
			setIsLoading(false);
		}, 2000);
	};

	// Navigation handlers
	const goToPrevPage = () => {
		setPageNumber((prev) => Math.max(prev - 1, 1));
	};

	const goToNextPage = () => {
		setPageNumber((prev) => Math.min(prev + 1, numPages || 1));
	};

	// Zoom handlers
	const handleZoomIn = () => {
		setFitMode(null); // Exit fit mode
		const currentIndex = ZOOM_LEVELS.indexOf(zoom);
		if (currentIndex < ZOOM_LEVELS.length - 1) {
			setZoom(ZOOM_LEVELS[currentIndex + 1]);
		} else if (currentIndex === -1) {
			// Not in predefined levels, find closest higher level
			const nextLevel = ZOOM_LEVELS.find(level => level > zoom) || ZOOM_LEVELS[ZOOM_LEVELS.length - 1];
			setZoom(nextLevel);
		}
	};

	const handleZoomOut = () => {
		setFitMode(null); // Exit fit mode
		const currentIndex = ZOOM_LEVELS.indexOf(zoom);
		if (currentIndex > 0) {
			setZoom(ZOOM_LEVELS[currentIndex - 1]);
		} else if (currentIndex === -1) {
			// Not in predefined levels, find closest lower level
			const prevLevel = [...ZOOM_LEVELS].reverse().find(level => level < zoom) || ZOOM_LEVELS[0];
			setZoom(prevLevel);
		}
	};

	const handleFitHeight = () => {
		setFitMode("height");
		setZoom(1); // Reset zoom for display
	};

	const handleFitWidth = () => {
		setFitMode("width");
		setZoom(1); // Reset zoom for display
	};

	// Keyboard navigation
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			switch (e.key) {
				case "ArrowLeft":
					if (pageNumber > 1) goToPrevPage();
					break;
				case "ArrowRight":
					if (pageNumber < (numPages || 0)) goToNextPage();
					break;
				case "+":
				case "=":
					if (e.ctrlKey || e.metaKey) {
						e.preventDefault();
						handleZoomIn();
					}
					break;
				case "-":
					if (e.ctrlKey || e.metaKey) {
						e.preventDefault();
						handleZoomOut();
					}
					break;
				case "0":
					if (e.ctrlKey || e.metaKey) {
						e.preventDefault();
						handleFitHeight();
					}
					break;
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [pageNumber, numPages, goToPrevPage, goToNextPage, handleZoomIn, handleZoomOut, handleFitHeight]);


	return (
		<div
			ref={containerRef}
			className="relative h-full w-full flex flex-col"
		>
			{/* Top navigation toolbar */}
			<div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 bg-black/50 backdrop-blur-sm rounded-lg p-2 flex items-center gap-2">
				{/* Page navigation */}
				{numPages && numPages > 1 && (
					<>
						<Button
							variant="ghost"
							size="icon"
							onClick={goToPrevPage}
							className="text-white hover:bg-white/20 h-8 w-8"
							disabled={pageNumber <= 1}
							title="Vorherige Seite"
						>
							<ChevronLeft className="h-4 w-4" />
						</Button>

						<div className="text-white text-sm font-medium px-2 text-center">
							{pageNumber} von {numPages}
						</div>

						<Button
							variant="ghost"
							size="icon"
							onClick={goToNextPage}
							className="text-white hover:bg-white/20 h-8 w-8"
							disabled={pageNumber >= numPages}
							title="Nächste Seite"
						>
							<ChevronRight className="h-4 w-4" />
						</Button>

						<div className="w-px h-6 bg-white/30 mx-1" />
					</>
				)}

				{/* Zoom controls */}
				<Button
					variant="ghost"
					size="icon"
					onClick={handleZoomOut}
					className="text-white hover:bg-white/20 h-8 w-8"
					disabled={!fitMode && zoom <= ZOOM_LEVELS[0]}
					title="Verkleinern (Strg+-)"
				>
					<ZoomOut className="h-4 w-4" />
				</Button>

				<div className="text-white text-sm font-medium px-3 min-w-[60px] text-center">
					{Math.round((fitMode ? 1 : zoom) * 100)}%
				</div>

				<Button
					variant="ghost"
					size="icon"
					onClick={handleZoomIn}
					className="text-white hover:bg-white/20 h-8 w-8"
					disabled={!fitMode && zoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
					title="Vergrössern (Strg++)"
				>
					<ZoomIn className="h-4 w-4" />
				</Button>

				<div className="w-px h-6 bg-white/30 mx-1" />

				<Button
					variant="ghost"
					size="icon"
					onClick={handleFitHeight}
					className={`text-white hover:bg-white/20 h-8 w-8 ${fitMode === "height" ? "bg-white/20" : ""}`}
					title="An Höhe anpassen (Strg+0)"
				>
					<MoveVertical className="h-4 w-4" />
				</Button>

				<Button
					variant="ghost"
					size="icon"
					onClick={handleFitWidth}
					className={`text-white hover:bg-white/20 h-8 w-8 ${fitMode === "width" ? "bg-white/20" : ""}`}
					title="An Breite anpassen"
				>
					<MoveHorizontal className="h-4 w-4" />
				</Button>

				<div className="w-px h-6 bg-white/30 mx-1" />

				<Button
					variant="ghost"
					size="icon"
					onClick={async () => {
						if (getResolvedPrintMode() === "desktop") {
							const res = await fetch(url);
							const blob = await res.blob();
							await dispatchPrint({
								documentType: "default",
								documentData: blob,
								fileName: "Dokument.pdf",
							});
						} else {
							const res = await fetch(url);
							const blob = await res.blob();
							const blobUrl = URL.createObjectURL(blob);
							const w = window.open(blobUrl, "_blank");
							if (w) {
								w.focus();
								w.print();
								setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
							}
						}
					}}
					className="text-white hover:bg-white/20 h-8 w-8"
					title="Drucken"
				>
					<Printer className="h-4 w-4" />
				</Button>

				{onDownload && (
					<Button
						variant="ghost"
						size="icon"
						onClick={onDownload}
						className="text-white hover:bg-white/20 h-8 w-8"
						aria-label="Datei herunterladen"
						data-testid="file-viewer-download-button"
						title="Herunterladen"
					>
						<Download className="h-4 w-4" />
					</Button>
				)}
			</div>

			{/* PDF content area */}
			<div className="flex-1 flex items-center justify-center overflow-auto">
				<Document
					file={url}
					onLoadSuccess={onDocumentLoadSuccess}
					loading={
						<div className="flex items-center justify-center">
							<Loader2 className="h-8 w-8 animate-spin text-white" />
						</div>
					}
					onLoadError={onDocumentLoadError}
					error={
						!isLoading ? (
						<div className="text-red-500">
								Fehler beim Laden der PDF-Datei
							</div>
						) : (
							<div className="flex items-center justify-center">
							<Loader2 className="h-8 w-8 animate-spin text-white" />
						</div>
						)
					}
				>
					<Page
						pageNumber={pageNumber}
						height={pageHeight || undefined}
						width={pageWidth || undefined}
						renderTextLayer={true}
						renderAnnotationLayer={true}
						className="shadow-2xl"
						onRenderSuccess={onPageRenderSuccess}
					/>
				</Document>
			</div>
		</div>
	);
};